import React, { useEffect, useState } from 'react';

/**
 * CelebrationsPopup
 *
 * Shows on the Presenter Dashboard whenever there are unread birthday or
 * anniversary greetings for today. Connects the CRM contact database with
 * the on-air experience so presenters can shout out listeners by name.
 *
 * Behaviour:
 *   • Fetches /api/celebrations/today on mount (which auto-generates if empty).
 *   • Auto-opens if any greeting has not been "displayed" yet.
 *   • Lets the presenter flip between EN/FR text, copy to clipboard, and
 *     tap "Read on air" — that POSTs /mark-displayed and removes the card.
 *   • Re-checks every 5 minutes so a freshly-added contact (Genevieve types
 *     it in mid-show) lights up without a refresh.
 *   • Persists "snoozed" state in sessionStorage so closing and re-opening
 *     a tab during the same shift doesn't re-pop after every hop.
 */
const CelebrationsPopup = ({ language = 'en' }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [items, setItems] = useState([]);   // un-displayed greetings
  const [allItems, setAllItems] = useState([]); // including displayed (for the bell badge)
  const [open, setOpen] = useState(false);
  const [snoozed, setSnoozed] = useState(() => sessionStorage.getItem('celebrationsSnoozed') === '1');
  const [textLang, setTextLang] = useState(language === 'fr' ? 'fr' : 'en');
  const [copiedId, setCopiedId] = useState(null);

  const fetchToday = async () => {
    try {
      const res = await fetch(`${backendUrl}/api/celebrations/today`);
      if (!res.ok) return;
      const data = await res.json();
      const list = data.celebrations || [];
      setAllItems(list);
      const pending = list.filter(c => !c.displayed);
      setItems(pending);
      // Auto-open the popup the first time the presenter loads in and there
      // are pending greetings (unless they explicitly snoozed in this session).
      if (pending.length > 0 && !snoozed) setOpen(true);
    } catch (e) {
      console.error('Failed to fetch celebrations:', e);
    }
  };

  useEffect(() => {
    fetchToday();
    const id = setInterval(fetchToday, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const markDisplayed = async (greetingId) => {
    try {
      await fetch(`${backendUrl}/api/celebrations/${greetingId}/mark-displayed`, { method: 'POST' });
      setItems(prev => prev.filter(c => c.id !== greetingId));
      setAllItems(prev => prev.map(c => c.id === greetingId ? { ...c, displayed: true } : c));
    } catch (e) {
      console.error('Failed to mark displayed:', e);
    }
  };

  const copyText = async (greeting) => {
    const txt = textLang === 'fr' ? greeting.greeting_french : greeting.greeting_english;
    const emoji = greeting.celebration_type === 'anniversary' ? '💑✨' : '🎂🎉';
    try {
      await navigator.clipboard.writeText(`${emoji} ${txt}`);
      setCopiedId(greeting.id);
      setTimeout(() => setCopiedId(null), 1800);
    } catch (e) {
      console.error('Copy failed:', e);
    }
  };

  const closeAndSnooze = () => {
    setOpen(false);
    setSnoozed(true);
    sessionStorage.setItem('celebrationsSnoozed', '1');
  };

  const reopen = () => {
    setOpen(true);
    setSnoozed(false);
    sessionStorage.removeItem('celebrationsSnoozed');
  };

  const pendingCount = items.length;
  const totalCount = allItems.length;
  if (totalCount === 0) return null;

  return (
    <>
      {/* Floating bell — always visible when there are any celebrations today */}
      <button
        type="button"
        onClick={reopen}
        title={pendingCount ? `${pendingCount} listener celebration${pendingCount > 1 ? 's' : ''} to read on air` : `${totalCount} celebration${totalCount > 1 ? 's' : ''} today`}
        data-testid="celebrations-floating-bell"
        className={`fixed bottom-5 right-5 z-40 rounded-full shadow-2xl border-2 border-white px-4 py-3 text-white font-semibold transition-all
          ${pendingCount > 0
            ? 'bg-gradient-to-br from-pink-500 to-amber-500 hover:scale-105 animate-pulse'
            : 'bg-gradient-to-br from-emerald-600 to-emerald-700 hover:scale-105'}`}
      >
        <span className="mr-2 text-xl">🎂</span>
        {pendingCount > 0
          ? `${pendingCount} to read on air`
          : `Celebrations (${totalCount})`}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="celebrations-popup"
          onClick={closeAndSnooze}
        >
          <div
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[88vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-pink-500 via-amber-500 to-emerald-500 text-white p-6 rounded-t-2xl flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2">
                  <span className="text-3xl">🎉</span>
                  Today's listener celebrations
                </h2>
                <p className="text-white/90 text-sm mt-1">
                  Read these out on air — let our listeners know we remember them.
                </p>
              </div>
              <button
                onClick={closeAndSnooze}
                aria-label="Close"
                data-testid="celebrations-popup-close"
                className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/30 flex items-center justify-center text-white transition"
              >✕</button>
            </div>

            {/* Language toggle */}
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-amber-50">
              <div className="text-sm text-gray-700">
                <strong>{pendingCount}</strong> waiting · {totalCount - pendingCount} already read
              </div>
              <div className="inline-flex rounded-full border border-gray-300 overflow-hidden text-sm" data-testid="celebrations-lang-toggle">
                <button
                  type="button"
                  onClick={() => setTextLang('en')}
                  className={`px-3 py-1 ${textLang === 'en' ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                >EN</button>
                <button
                  type="button"
                  onClick={() => setTextLang('fr')}
                  className={`px-3 py-1 ${textLang === 'fr' ? 'bg-emerald-600 text-white' : 'text-gray-700 hover:bg-gray-100'}`}
                >FR</button>
              </div>
            </div>

            {/* Cards */}
            <div className="p-6 space-y-4">
              {pendingCount === 0 && (
                <div className="text-center py-10 text-gray-500" data-testid="celebrations-empty">
                  <div className="text-5xl mb-2">✅</div>
                  <p>All of today's greetings have been read on air. Great work!</p>
                </div>
              )}

              {items.map((c) => {
                const greeting = textLang === 'fr' ? c.greeting_french : c.greeting_english;
                const isAnn = c.celebration_type === 'anniversary';
                return (
                  <div
                    key={c.id}
                    data-testid={`celebration-card-${c.id}`}
                    className={`relative rounded-xl p-5 border-2 ${isAnn ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'}`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="text-3xl">{isAnn ? '💑' : '🎂'}</div>
                      <div className="flex-1">
                        <div className="font-bold text-gray-900 text-lg">
                          {c.contact_name || c.recipient_name || 'Listener'}
                        </div>
                        <div className="text-xs text-gray-600 uppercase tracking-wide mt-0.5">
                          {isAnn ? 'Anniversary' : 'Birthday'}
                          {c.location ? ` • ${c.location}` : ''}
                        </div>
                      </div>
                    </div>

                    <p className="text-gray-800 leading-relaxed whitespace-pre-line text-base">
                      {greeting || 'No greeting available.'}
                    </p>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => markDisplayed(c.id)}
                        data-testid={`celebration-read-${c.id}`}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 font-semibold"
                      >
                        ✓ Read on air
                      </button>
                      <button
                        onClick={() => copyText(c)}
                        data-testid={`celebration-copy-${c.id}`}
                        className="px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {copiedId === c.id ? '✓ Copied!' : '📋 Copy'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-6 py-4 border-t border-gray-100 flex justify-between items-center text-sm text-gray-500">
              <span>Tip: greetings come from the CRM contact database — birthdays you add there appear here automatically.</span>
              <button
                onClick={closeAndSnooze}
                className="text-gray-700 hover:text-gray-900 underline"
                data-testid="celebrations-popup-snooze"
              >Snooze for this shift</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CelebrationsPopup;
